/**
 * PaymentListItem Component
 * 결제 내역 아이템
 */
import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';

interface PaymentListItemProps {
  payment: {
    paymentId: string;
    uid: string;
    merchantUid: string;
    paymentType: string;
    productName: string;
    amount: number;
    status: string;
    createdAt: Date;
    completedAt?: Date;
    user?: {
      displayName: string;
      email: string;
    };
  };
}

const STATUS_LABELS: Record<string, string> = {
  pending: '대기중',
  completed: '완료',
  failed: '실패',
  refunded: '환불'
};

const STATUS_COLORS: Record<string, string> = {
  pending: tokens.colors.semantic.warning,
  completed: tokens.colors.semantic.success,
  failed: tokens.colors.semantic.error,
  refunded: tokens.colors.neutral[500]
};

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  oneTime: '일회 구매',
  subscription: '구독'
};

export const PaymentListItem: React.FC<PaymentListItemProps> = ({ payment }) => {
  const formatDate = (date: Date | undefined | null) => {
    // 날짜가 유효한지 확인
    if (!date || isNaN(date.getTime())) {
      console.error('[PaymentListItem] Invalid date:', date);
      return '-';
    }

    try {
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('[PaymentListItem] Date format error:', error, date);
      return '-';
    }
  };

  return (
    <Item>
      <MainInfo>
        <ProductName>{payment.productName}</ProductName>
        <MerchantUid>{payment.merchantUid}</MerchantUid>
        {payment.user && (
          <UserInfo>
            <UserName>{payment.user.displayName}</UserName>
            <UserEmail>{payment.user.email}</UserEmail>
          </UserInfo>
        )}
      </MainInfo>

      <Details>
        <DetailItem>
          <Label>결제 유형</Label>
          <Value>{PAYMENT_TYPE_LABELS[payment.paymentType] || payment.paymentType}</Value>
        </DetailItem>

        <DetailItem>
          <Label>금액</Label>
          <Amount>{payment.amount.toLocaleString()}원</Amount>
        </DetailItem>

        <DetailItem>
          <Label>상태</Label>
          <StatusBadge $color={STATUS_COLORS[payment.status]}>
            {STATUS_LABELS[payment.status] || payment.status}
          </StatusBadge>
        </DetailItem>

        <DetailItem>
          <Label>결제일</Label>
          <Value>{formatDate(payment.createdAt)}</Value>
        </DetailItem>

        {payment.completedAt && (
          <DetailItem>
            <Label>완료일</Label>
            <Value>{formatDate(payment.completedAt)}</Value>
          </DetailItem>
        )}
      </Details>
    </Item>
  );
};

const Item = styled.div`
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  display: flex;
  gap: ${tokens.spacing[4]};
  align-items: center;
  transition: all ${tokens.animation.duration.fast} ${tokens.animation.easing.easeOut};

  &:hover {
    box-shadow: ${tokens.shadows.sm};
    border-color: ${tokens.colors.neutral[300]};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MainInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const ProductName = styled.div`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[1]};
`;

const MerchantUid = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[400]};
  font-family: ${tokens.typography.fontFamily.mono};
`;

const UserInfo = styled.div`
  margin-top: ${tokens.spacing[2]};
  padding-top: ${tokens.spacing[2]};
  border-top: 1px solid ${tokens.colors.neutral[100]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const UserName = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const UserEmail = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[500]};
`;

const Details = styled.div`
  flex: 2;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${tokens.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const Label = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[500]};
`;

const Value = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const Amount = styled(Value)`
  color: ${tokens.colors.primary[600]};
  font-weight: ${tokens.typography.fontWeight.bold};
`;

const StatusBadge = styled.div<{ $color: string }>`
  display: inline-block;
  padding: ${tokens.spacing[1]} ${tokens.spacing[2]};
  background-color: ${props => `${props.$color}15`};
  color: ${props => props.$color};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.semibold};
  width: fit-content;
`;
